/**
 * Chat Window v2 - Unified Thinking Mode (Now vs Next)
 *
 * Access: http://flownotes.localhost/?test=chat-window
 * Legacy V0 tabs: /?test=chat-window-legacy
 *
 * Design: docs/design/maater-chat-thinking-mode.md
 * Design ownership: docs/product/bets/2026-07-19-mobile-chat-thinking-mode.md
 * Production: docs/product/bets/2026-07-24-chat-live-thinking-timeline-mobile-web.md
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  MoreHorizontal,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type Panel = 'now' | 'next' | 'live-history' | 'notes'

const PANELS: { id: Panel; label: string }[] = [
  { id: 'now', label: 'Now (live noise)' },
  { id: 'next', label: 'Next (Thinking)' },
  { id: 'live-history', label: 'Live vs History' },
  { id: 'notes', label: 'Notes' },
]

const USER_PROMPT =
  'Find a recipe for sourdough pancakes. Have I made this before? Personal recipes'

type MorphPhase =
  | 'looking_up'
  | 'no_docs'
  | 'web_search'
  | 'fetch_web'
  | 'reading'
  | 'settled'

const MORPH_SEQUENCE: { phase: MorphPhase; label: string; ms: number }[] = [
  { phase: 'looking_up', label: 'Looking up documents about sourdough pancakes', ms: 0 },
  { phase: 'no_docs', label: 'No matching documents for sourdough pancakes', ms: 1400 },
  { phase: 'web_search', label: 'Searching the web for sourdough pancakes', ms: 2800 },
  { phase: 'fetch_web', label: 'Opening recipe page', ms: 4200 },
  { phase: 'reading', label: 'Reading "Spill: sourdough pancake recipe"', ms: 5600 },
  {
    phase: 'settled',
    label: 'Looked through documents and the web · 4 steps',
    ms: 7000,
  },
]

const NEXT_STEPS = [
  {
    id: 's1',
    label: 'Search documents',
    footnote: '"sourdough pancakes" · 0 matches',
  },
  {
    id: 's2',
    label: 'Web search',
    footnote: 'sourdough pancakes',
    details: true,
  },
  {
    id: 's3',
    label: 'Open page',
    footnote: 'example.com/sourdough-pancakes',
    details: true,
  },
  {
    id: 's4',
    label: 'Spill read',
    footnote: '~2113 tokens',
  },
]

const MOCK_DOCS = [
  {
    id: 'd1',
    title: 'Weekend brunch notes',
    path: 'personal/recipes/brunch-notes.md',
  },
  {
    id: 'd2',
    title: 'Sourdough starter log',
    path: 'personal/recipes/sourdough-starter.md',
  },
]

function PhoneChrome({
  children,
  title = 'New Conversation',
  subtitle = 'Personal +1',
}: {
  children: ReactNode
  title?: string
  subtitle?: string
}) {
  return (
    <div
      className="mx-auto flex h-[720px] w-[360px] flex-col overflow-hidden rounded-[28px] border border-border bg-background shadow-xl"
      data-testid="chat-thinking-phone"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-sm font-semibold text-foreground">
            <span className="truncate">{title}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3">{children}</div>
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-2 rounded-full border border-border bg-muted/40 px-3 py-2">
          <span className="flex-1 text-sm text-muted-foreground">Ask anything...</span>
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
            <ChevronRight className="h-4 w-4 rotate-[-90deg] text-muted-foreground" />
          </div>
        </div>
      </div>
    </div>
  )
}

function UserBubble({ text }: { text: string }) {
  return (
    <div className="ml-8 rounded-2xl bg-primary px-3.5 py-2.5 text-sm leading-relaxed text-primary-foreground">
      {text}
    </div>
  )
}

function NowToolCard({
  title,
  footnote,
  details,
}: {
  title: string
  footnote?: string
  details?: boolean
}) {
  return (
    <div className="rounded-xl border border-border bg-card px-3 py-2.5 shadow-sm">
      <div className="flex items-center gap-2">
        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/15">
          <Check className="h-3 w-3 text-emerald-600" />
        </div>
        <span className="text-sm font-medium text-foreground">{title}</span>
      </div>
      {footnote ? (
        <p className="mt-1.5 pl-7 text-xs text-muted-foreground">{footnote}</p>
      ) : null}
      {details ? (
        <button
          type="button"
          className="mt-1.5 flex items-center gap-1 pl-7 text-xs text-muted-foreground"
        >
          <ChevronRight className="h-3 w-3" />
          Technical details
        </button>
      ) : null}
    </div>
  )
}

function NowPanel() {
  return (
    <PhoneChrome>
      <UserBubble text={USER_PROMPT} />
      <NowToolCard
        title="Searching: sourdough pancakes"
        footnote={'"sourdough pancakes" · 0 matches'}
      />
      <NowToolCard title="Using tool: webSearch" details />
      <NowToolCard
        title="Searching: sourdough pancakes"
        footnote={'"sourdough pancakes" · 0 matches'}
      />
      <NowToolCard title="Using tool: fetchWebPage" details />
      <NowToolCard title="Spill read" footnote="Spill read · ~2113 tokens" />
      <p className="text-sm leading-relaxed text-foreground">
        Let me grab the top recipe and also do a broader search just to be sure.
        Working on it...
      </p>
      <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        Network request failed
      </div>
    </PhoneChrome>
  )
}

function ThinkingBlock({
  morphIndex,
  playing,
}: {
  morphIndex: number
  playing: boolean
}) {
  const [expanded, setExpanded] = useState(true)
  const [openDetails, setOpenDetails] = useState<string | null>(null)
  const current = MORPH_SEQUENCE[Math.min(morphIndex, MORPH_SEQUENCE.length - 1)]
  const settled = current.phase === 'settled'
  const inFlight = playing && !settled
  const visibleSteps = NEXT_STEPS.slice(
    0,
    settled ? NEXT_STEPS.length : Math.min(morphIndex + 1, NEXT_STEPS.length),
  )

  return (
    <div
      className="overflow-hidden rounded-xl border border-border bg-muted/20"
      data-testid="thinking-block"
    >
      <button
        type="button"
        className="flex w-full items-start gap-2 px-3 py-2.5 text-left"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        {inFlight ? (
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" />
        ) : (
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        )}
        <span className="min-w-0 flex-1 text-sm leading-snug text-foreground">
          {current.label}
        </span>
        <ChevronRight
          className={cn(
            'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            expanded && 'rotate-90',
          )}
        />
      </button>

      {expanded ? (
        <div className="border-t border-border">
          {/* Cursor-style max-height scroll cell - steps slip through */}
          <div
            className="max-h-[240px] space-y-1 overflow-y-auto px-2 py-2"
            data-testid="thinking-scroll-viewport"
          >
            {visibleSteps.map((step) => (
              <div
                key={step.id}
                className="group flex items-start gap-2 rounded-md px-1.5 py-1.5 hover:bg-muted/50"
              >
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground">{step.label}</div>
                  <p className="text-xs text-muted-foreground">{step.footnote}</p>
                  {openDetails === step.id ? (
                    <pre className="mt-1 overflow-x-auto rounded-md bg-muted p-2 text-[10px] text-muted-foreground">
                      {`{ "tool": "${step.label}", "ok": true }`}
                    </pre>
                  ) : null}
                </div>
                {step.details ? (
                  <button
                    type="button"
                    className="shrink-0 rounded p-1 text-muted-foreground opacity-70 hover:bg-muted hover:opacity-100"
                    aria-label={`Details for ${step.label}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      setOpenDetails((id) => (id === step.id ? null : step.id))
                    }}
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>
            ))}

            {settled ? (
              <div className="mt-1 rounded-md border border-border/60 bg-card/50">
                <div className="px-2 py-1 text-[11px] font-semibold text-muted-foreground">
                  Found {MOCK_DOCS.length} related documents
                </div>
                {MOCK_DOCS.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-2 border-t border-border/60 px-2 py-1.5"
                  >
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-semibold text-foreground">
                        {doc.title}
                      </div>
                      <div className="truncate text-[11px] text-muted-foreground">
                        {doc.path}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function NextPanel() {
  const [morphIndex, setMorphIndex] = useState(0)
  const [playing, setPlaying] = useState(true)
  const [replayKey, setReplayKey] = useState(0)

  useEffect(() => {
    if (!playing) return
    setMorphIndex(0)
    const timers = MORPH_SEQUENCE.slice(1).map((step, i) =>
      window.setTimeout(() => setMorphIndex(i + 1), step.ms),
    )
    const stop = window.setTimeout(() => setPlaying(false), 7200)
    return () => {
      timers.forEach(clearTimeout)
      clearTimeout(stop)
    }
  }, [playing, replayKey])

  const settled = morphIndex >= MORPH_SEQUENCE.length - 1 && !playing

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setPlaying(true)
            setReplayKey((k) => k + 1)
          }}
        >
          Replay morph
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setMorphIndex(MORPH_SEQUENCE.length - 1)
            setPlaying(false)
          }}
        >
          Jump to settled
        </Button>
      </div>
      <PhoneChrome>
        <UserBubble text={USER_PROMPT} />
        <ThinkingBlock morphIndex={morphIndex} playing={playing} />
        {settled ? (
          <>
            <p className="text-sm leading-relaxed text-foreground">
              No personal recipe matched &quot;sourdough pancakes&quot; in your
              library. I pulled a solid web recipe and parked the details in spill
              context - want me to save a note under personal/recipes?
            </p>
            <div className="rounded-xl border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
              Flash cards / proposed edits / action choices stay{' '}
              <span className="font-semibold text-foreground">outside</span> this
              Thinking block.
            </div>
            <div className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Network request failed (one banner under Thinking - not per tool)</span>
            </div>
          </>
        ) : null}
      </PhoneChrome>
    </div>
  )
}

function LiveVsHistoryPanel() {
  return (
    <div className="space-y-4">
      <p className="mx-auto max-w-3xl text-center text-sm text-muted-foreground">
        Same turn, two render paths. Live keeps tool cards in memory; history
        rehydrate only has Thinking + final prose (tools are not persisted).
      </p>
      <div className="flex flex-wrap items-start justify-center gap-8">
        <div className="space-y-2">
          <p className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Live (unfinished)
          </p>
          <NowPanel />
        </div>
        <div className="space-y-2">
          <p className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            History reopen (target quiet)
          </p>
          <PhoneChrome title="Exam Run 13..." subtitle="Rowan & Alice Space">
            <UserBubble text="Which areas do i need to study more?" />
            <div className="rounded-xl border border-border bg-muted/20 px-3 py-2">
              <button
                type="button"
                className="flex w-full items-center gap-2 text-left text-sm text-muted-foreground"
              >
                <ChevronRight className="h-3.5 w-3.5" />
                Thinking
              </button>
            </div>
            <p className="text-sm leading-relaxed text-foreground">
              Let me pull your latest quiz run details to see exactly what you
              missed. Good - I now have the full picture from the quiz engine.
              Here&apos;s your real data:
            </p>
            <p className="text-sm font-semibold text-foreground">
              Areas to focus on
            </p>
            <p className="text-sm text-muted-foreground">
              Priority 1: hpa_disclosure - trending down...
            </p>
          </PhoneChrome>
        </div>
      </div>
    </div>
  )
}

function NotesPanel() {
  return (
    <div className="mx-auto max-w-2xl space-y-4 text-sm leading-relaxed text-foreground">
      <p>
        Canonical design:{' '}
        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
          docs/design/maater-chat-thinking-mode.md
        </code>
      </p>
      <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
        <li>
          <strong className="text-foreground">Now</strong> recreates the noisy
          mobile tool stack from the sourdough dogfood screenshot.
        </li>
        <li>
          <strong className="text-foreground">Next</strong> is unified Thinking:
          morphing one-liner, max-height scroll step log, details via{' '}
          <code className="text-xs">...</code> only, answer outside.
        </li>
        <li>
          Legacy Basecamp <code className="text-xs">ThinkingModePanel</code> is a
          different surface - do not conflate.
        </li>
        <li>
          Production (Expo + web): bet{' '}
          <code className="text-xs">
            2026-07-24-chat-live-thinking-timeline-mobile-web.md
          </code>
          . Design ownership:{' '}
          <code className="text-xs">2026-07-19-mobile-chat-thinking-mode.md</code>.
        </li>
        <li>
          Older V0 chat-window tabs:{' '}
          <a className="text-primary underline" href="/?test=chat-window-legacy">
            /?test=chat-window-legacy
          </a>
        </li>
      </ul>
    </div>
  )
}

export function ChatWindowThinkingTest() {
  const [panel, setPanel] = useState<Panel>('next')
  const body = useMemo(() => {
    if (panel === 'now') return <NowPanel />
    if (panel === 'live-history') return <LiveVsHistoryPanel />
    if (panel === 'notes') return <NotesPanel />
    return <NextPanel />
  }, [panel])

  return (
    <div
      className="min-h-screen bg-background text-foreground"
      data-testid="chat-window-thinking-prototype"
    >
      <header className="border-b border-border px-6 py-4">
        <div className="mx-auto flex max-w-5xl flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Prototype · Chat window v2
            </p>
            <h1 className="text-xl font-semibold">Unified Thinking Mode</h1>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Grounded Now (noisy tool cards) vs Next (one morphing Thinking
              block). Extends maater-design-ui every-frame craft.
            </p>
          </div>
          <a
            href="/?test=index"
            className="text-sm text-muted-foreground underline-offset-2 hover:underline"
          >
            Prototype index
          </a>
        </div>
        <div className="mx-auto mt-4 flex max-w-5xl gap-2">
          {PANELS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPanel(p.id)}
              className={cn(
                'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                panel === p.id
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-card text-muted-foreground hover:text-foreground',
              )}
              data-testid={`chat-thinking-tab-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-8">{body}</main>
    </div>
  )
}
